import { useEffect, useState } from 'react';
import { api, getAdminToken } from '../api.ts';
import type { JoinedSession } from '../App.tsx';

interface Props {
  sessionId: string;
  onJoined: (session: JoinedSession) => void;
}

/**
 * Everyone entering a review link identifies themselves first — not for security, but so
 * every note and every approve/reject carries a name someone can follow up with.
 */
export function JoinPage({ sessionId, onJoined }: Props): React.JSX.Element {
  const storedName = localStorage.getItem('openreview.name')?.trim() ?? '';
  const [name, setName] = useState(storedName);
  const [password, setPassword] = useState('');
  const [needsPassword, setNeedsPassword] = useState(false);
  const [sessionName, setSessionName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState(true);

  // The host holds the admin token and is "joined" the instant they open the link — but the
  // first time round they have no name yet, and would otherwise be announced to the room as the
  // bare "api". So the token holder gets the same name prompt as everyone else, once, and the
  // name they pick is remembered and sent on every later request.
  const isAdmin = Boolean(getAdminToken());

  useEffect(() => {
    let cancelled = false;
    void api
      .getSession(sessionId)
      .then((res) => {
        if (cancelled) return;
        setNeedsPassword(res.hasPassword);
        setSessionName(res.session.name);
        // An unexpired cookie (or the admin token) means we can go straight in — unless we are
        // the host arriving without a chosen name yet, in which case fall through to the prompt.
        if (res.joined && res.items && res.you && !(isAdmin && !storedName)) {
          onJoined({
            name: res.you,
            items: res.items,
            playlistId: res.session.playlistId,
            playlistName: res.playlist?.name ?? res.session.name,
            manualOrder: Boolean(res.playlist?.manual_order),
            canDownload: res.canDownload,
          });
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setChecking(false);
      });
    return () => {
      cancelled = true;
    };
  }, [sessionId, onJoined, isAdmin, storedName]);

  const join = async (): Promise<void> => {
    if (!name.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      localStorage.setItem('openreview.name', name.trim());
      if (isAdmin) {
        // The host is already authorised by the token — no password to clear. Re-probing the
        // session now that the name is stored re-stamps the guest cookie (which the WebSocket
        // reuses) with it, so the room announces the real name instead of "api".
        const res = await api.getSession(sessionId);
        if (!res.items) throw new Error('session has no clips to review');
        onJoined({
          name: res.you ?? name.trim(),
          items: res.items,
          playlistId: res.session.playlistId,
          playlistName: res.playlist?.name ?? res.session.name,
          manualOrder: Boolean(res.playlist?.manual_order),
          canDownload: res.canDownload,
        });
        return;
      }
      const res = await api.joinSession(sessionId, {
        name: name.trim(),
        password: password || undefined,
      });
      localStorage.setItem('openreview.name', res.you);
      onJoined({
        name: res.you,
        items: res.items,
        playlistId: res.session.playlistId,
        playlistName: res.playlist.name,
        manualOrder: Boolean(res.playlist.manual_order),
        canDownload: res.canDownload,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  if (checking) return <div className="centered muted">loading…</div>;

  return (
    <div className="centered">
      <form
        className="card"
        onSubmit={(e) => {
          e.preventDefault();
          void join();
        }}
      >
        <h1>Join review</h1>
        {sessionName ? <p className="muted">{sessionName}</p> : null}

        <label>
          Your name
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Min"
            autoFocus
            required
          />
        </label>

        {needsPassword && !isAdmin ? (
          <label>
            Password
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
          </label>
        ) : null}

        {error ? <p className="error">{error}</p> : null}

        <button type="submit" className="primary" disabled={busy || !name.trim()}>
          {busy ? 'connecting…' : 'Join'}
        </button>
      </form>
    </div>
  );
}
